import { Table, ConfigProvider, Button, Input, Select, Card, Pagination, Modal, Spin } from "antd";
import { FileTextOutlined, SearchOutlined } from "@ant-design/icons";
import { observer } from "mobx-react-lite";
import ApplicantDetailsModal from "./Modal/ApplicantDetailsModal";
import { useApplicantTracking } from "../../app/hooks/useApplicantTracking";
import { PaginatedResult } from "../../app/utils/pagination";
import { useState, useEffect } from "react";
import Debounce from "../../app/utils/debounce";
import { toast } from "react-toastify";
import dayjs from "dayjs";

const debouncer = new Debounce();
const { Option } = Select;

const ApplicantList = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [paginatedResult, setPaginatedResult] = useState(new PaginatedResult());
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState();
  const [selectedApplicantId, setSelectedApplicantId] = useState(null);
  const pageSize = 10;

  const positionOptions = Array.from(new Set((paginatedResult.data || []).map((job) => job.position).filter(Boolean)));

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);

    debouncer.debounce({
      debounce: () => {
        setCurrentPage(1);
        setDebouncedSearch(value);
      },
      debounceTimeout: 800,
    });
  };

  const { getApplicantTracking } = useApplicantTracking({
    pageSize: pageSize,
  });
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const result = await getApplicantTracking(currentPage, pageSize, debouncedSearch || selectedPosition);
        setPaginatedResult(result);
      } catch (error) {
        toast.error(error, {
          position: "top-right",
        });
        setPaginatedResult(new PaginatedResult());
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [currentPage, pageSize, debouncedSearch, selectedPosition]);

  const handleView = (id) => {
    setSelectedApplicantId(id);
    setIsModalVisible(true);
  };

  const columns = [
    { title: "NO.", dataIndex: "no", key: "no", align: "center", render: (_, __, index) => <span>{(currentPage - 1) * pageSize + index + 1}</span> },
    {
      title: "DATE",
      dataIndex: "createdAt",
      key: "createdAt",
      align: "createdAt",
      render: (date) => <p>{dayjs(date).format("MMM DD, YYYY")}</p>,
    },
    { title: "NAME", dataIndex: "fullName", key: "fullName", align: "center" },
    { title: "POSITION", dataIndex: "position", key: "position", align: "center" },
    {
      title: "",
      key: "action",
      align: "center",
      render: (_, record) => (
        <Button
          type="primary"
          className="mx-auto flex items-center hover:opacity-80"
          style={{ backgroundColor: "#F97316", borderColor: "#F97316", fontWeight: "600", borderRadius: "9999px" }}
          onClick={() => handleView(record.id)}
        >
          <FileTextOutlined /> VIEW
        </Button>
      ),
    },
  ];

  return (
    <>
      <Card className="rounded-2xl px-4 md:px-16 py-8 shadow-2xl w-full overflow-hidden">
        <div className="pb-6">
          <p className="font-medium text-3xl m-0">All Interested Applicants</p>
        </div>
        <div className="flex flex-col md:flex-row gap-4 md:gap-8 items-center mb-6">
          <div className="flex flex-col md:flex-row gap-4 flex-1">
            <Input
              size="large"
              placeholder="Search here"
              value={searchText}
              onChange={handleSearchChange}
              prefix={<SearchOutlined style={{ fontSize: "20px", color: "#666" }} />}
            />
            <Select
              size="large"
              placeholder="Search position"
              className="w-full md:w-64"
              value={selectedPosition}
              allowClear
              onChange={(value) => {
                setSelectedPosition(value);
                setCurrentPage(1);
              }}
            >
              {positionOptions.map((position) => (
                <Option key={position} value={position}>
                  {position}
                </Option>
              ))}
            </Select>
          </div>
          <h2 className="text-2xl font-medium m-0">{paginatedResult.totalItems} Applicants</h2>
        </div>

        <ConfigProvider
          theme={{
            components: {
              Table: { headerBg: "#364042", headerColor: "white" },
            },
          }}
        >
          {isLoading ? (
            <div className="col-span-3 flex justify-center py-20">
              <Spin tip="Fetching Job Openings..." />
            </div>
          ) : (
            <Table rowKey="id" columns={columns} dataSource={paginatedResult.data} pagination={false} />
          )}

          <div className="flex justify-center mt-8">
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              className="job-search-pagination"
              total={paginatedResult.totalItems}
              onChange={(page) => {
                setCurrentPage(page);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              showSizeChanger={false}
            />
          </div>
        </ConfigProvider>
      </Card>

      <Modal
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={1350}
        centered
        destroyOnClose
        styles={{ body: { padding: 0 } }}
      >
        <ApplicantDetailsModal
          applicantId={selectedApplicantId}
          onClose={() => {
            setIsModalVisible(false);
            setSelectedApplicantId(null);
          }}
        />
      </Modal>
    </>
  );
};

export default observer(ApplicantList);
